// This file is auto-generated. Do not edit manually.

import { z } from "zod";
import {
	CreateEntitySchema,
	EntityResponseSchema,
	EntitySchema,
} from "./entitySchemas";
import {
	CreateUserSchema,
	UserResponseSchema,
	UserRoleSchema,
	UserSchema,
} from "./userSchemas";
import {
	PaginationInfoSchema,
	ResponseMessageSchema,
	StatusSchema,
} from "./utilitySchemas";
import { SinglePriceListSchema } from "./pricelistSchemas";

export const InviteSchema = z.object({
	id: z.number().int(),
	token: z.string(),
	consumed: z.boolean(),
	user: z.object({
		id: z.number().int(),
		firstName: z.string(),
		lastName: z.string(),
		email: z.string().email(),
		phoneNumber: z.string(),
		priceList: SinglePriceListSchema,
		role: UserRoleSchema,
		entity: EntitySchema,
	}),
	createdBy: z
		.object({
			id: z.number().int(),
			firstName: z.string(),
			lastName: z.string(),
			email: z.string().email(),
			role: UserRoleSchema,
		})
		.optional(),
	createdAt: z.date(),
	updatedAt: z.date(),
});
export type Invite = z.infer<typeof InviteSchema>;

export const InviteCreateSchema = z.object({
	token: z.string(),
	userId: z.number().int(),
	createdById: z.number().int(),
});
export type InviteCreate = z.infer<typeof InviteCreateSchema>;

export const InviteQuerySchema = z.object({
	limit: z.coerce.number().int().positive().optional(),
	offset: z.coerce.number().int().nonnegative().optional(),
	sortBy: z
		.enum([
			"id",
			"consumed",
			"createdAt",
			"updatedAt",
			"firstName",
			"lastName",
			"email",
			"companyName",
			"role",
		])
		.optional(),
	sortOrder: z.enum(["asc", "desc"]).optional(),
	filter: z
		.object({
			consumed: z
				.union([
					z.boolean(),
					z.enum(["true", "false"]).transform((val) => val === "true"),
				])
				.optional(),
			userIsActive: z
				.preprocess((val) => {
					if (val === "true" || val === "1") return true;
					if (val === "false" || val === "0") return false;
					return val;
				}, z.boolean())
				.optional(),
			appraiser: z.string().optional(),
		})
		.optional(),
});
export type InviteQuery = z.infer<typeof InviteQuerySchema>;

export const InviteEntitySchema = z.object({
	entity: CreateEntitySchema,
	user: CreateUserSchema.omit({ password: true, entityId: true }),
});
export type InviteEntity = z.infer<typeof InviteEntitySchema>;

export const InviteExternalUserSchema = z.object({
	requestId: z.string(),
});

export type InviteExternalUser = z.infer<typeof InviteExternalUserSchema>;

export const InviteUserSchema = CreateUserSchema.omit({
	entityId: true,
	password: true,
});
export type InviteUser = z.infer<typeof InviteUserSchema>;

export const InviteAppraiserAdminSchema = CreateUserSchema.omit({
	password: true,
	podAdminId: true,
	priceListId: true,
	showPriceList: true,
	entityId: true,
}).extend({
	position: z.string().min(2),
	appraiserId: z.string(),
});

export type InviteAppraiserAdmin = z.infer<typeof InviteAppraiserAdminSchema>;

export const InviteExternalUserPayloadSchema = CreateUserSchema.omit({
	password: true,
});
export type InviteExternalUserPayload = z.infer<
	typeof InviteExternalUserPayloadSchema
>;

export const InviteByTokenSchema = z.object({
	token: z.string(),
});
export type InviteByToken = z.infer<typeof InviteByTokenSchema>;

export const InviteTokenCreateSchema = z.object({
	entityId: z.number().int(),
	userId: z.number().int(),
});
export type InviteTokenCreate = z.infer<typeof InviteTokenCreateSchema>;

export const InviteTokenSchema = z.object({
	entityId: z.number().int(),
	userId: z.number().int(),
});
export type InviteToken = z.infer<typeof InviteTokenSchema>;

/**
 * DOC: RESPONSE SCHEMAS AND TYPES
 */

export const InviteEntityAndUserResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema,
	data: z
		.object({
			user: UserSchema,
			entity: EntitySchema,
			token: z.string(),
		})
		.nullable(),
});
export type InviteEntityAndUserResponse = z.infer<
	typeof InviteEntityAndUserResponseSchema
>;

export const InviteUserResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema,
	data: z.null(),
});
export type InviteUserResponse = z.infer<typeof InviteUserResponseSchema>;

export const InviteAcceptSchema = z.object({
	user: UserResponseSchema,
	entity: EntityResponseSchema,
});

export const InviteAcceptResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema,
	data: InviteAcceptSchema,
});

export type AcceptInviteData = z.infer<typeof InviteAcceptSchema>;

export const GetInvitesResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema,
	data: z.object({
		invites: z.array(InviteSchema),
		paginationInfo: PaginationInfoSchema,
	}),
});

export type GetAllInvitesResponse = z.infer<typeof GetInvitesResponseSchema>;
